/**
 * 数据对接模块 API 服务层
 * 基于统一请求工具 @/lib/request 封装
 */

import { request, type RequestOptions } from '@/lib/request';

const PREFIX = '/data-integration';

function post<T = any>(path: string, data: Record<string, any> = {}, options?: RequestOptions): Promise<T> {
  return request<T>(`${PREFIX}${path}`, data, options);
}

// ─── Connection API ──────────────────────────────────────────────

export interface TestDbConnectionParams {
  host: string;
  port: number;
  username: string;
  password: string;
  database_name: string;
  db_type: 'mysql' | 'postgresql';
  ssl_enabled?: boolean;
}

export interface TestDbConnectionResult {
  connected: boolean;
  server_version?: string;
  response_time_ms?: number;
  error_message?: string;
}

export function testDbConnection(params: TestDbConnectionParams) {
  return post<TestDbConnectionResult>('/testDbConnection', params);
}

export interface SaveDbConnectionParams {
  connectionId?: string | null;
  connection_name: string;
  connection_type: 'source' | 'target' | 'production';
  db_type: 'mysql' | 'postgresql';
  host: string;
  port: number;
  username: string;
  password: string;
  database_name: string;
  ssl_enabled?: boolean;
  ssl_warning_accepted?: boolean;
}

export interface DbConnectionInfo {
  _id: string;
  connection_name: string;
  connection_type: string;
  db_type: string;
  host: string;
  port: number;
  username: string;
  database_name: string;
  ssl_enabled: boolean;
  connection_status: string;
  last_test_time?: number;
  last_test_error?: string;
  create_time: number;
  /** Source connections only — catalog summary fields */
  catalog_table_count?: number;
  catalog_version?: number;
  catalog_last_built?: number | null;
}

export function saveDbConnection(params: SaveDbConnectionParams) {
  return post<{ _id: string; connection_name: string; connection_status: string }>('/saveDbConnection', params);
}

export interface GetDbConnectionListParams {
  connection_type?: 'source' | 'target' | 'production';
  page?: number;
  pageSize?: number;
}

export function getDbConnectionList(params: GetDbConnectionListParams = {}) {
  return post<{ items: DbConnectionInfo[]; total: number; page: number; pageSize: number }>('/getDbConnectionList', params);
}

export function deleteDbConnection(connectionId: string) {
  return post<null>('/deleteDbConnection', { connectionId });
}

// ─── Catalog API ─────────────────────────────────────────────────

export interface BuildTableCatalogResult {
  catalog_version: number;
  table_count: number;
  column_count: number;
  foreign_key_count: number;
  duration_ms: number;
  new_tables: number;
  updated_tables: number;
  removed_tables: number;
}

export function buildTableCatalog(connectionId: string) {
  return post<BuildTableCatalogResult>('/buildTableCatalog', { connectionId });
}

export interface PipelineStatus {
  has_normalized_table: boolean;
  normalized_table_id: string | null;
  creation_status: string | null;
  last_sync_status: string | null;
  last_sync_time: number | null;
  has_prod_mapping: boolean;
  prod_mapping_status: string | null;
  last_write_time: number | null;
}

export interface TableCatalogItem {
  _id: string;
  table_name: string;
  table_comment: string;
  display_name: string;
  group_id: string | null;
  sync_order: number | null;
  row_count_estimate: number;
  column_count: number;
  has_foreign_keys: boolean;
  user_annotation: string;
  catalog_version: number;
  pipeline?: PipelineStatus;
}

export function getTableCatalog(params: {
  connectionId: string;
  keyword?: string;
  groupId?: string;
  page?: number;
  pageSize?: number;
}) {
  return post<{ items: TableCatalogItem[]; total: number; page: number; pageSize: number }>('/getTableCatalog', params);
}

export interface ColumnMetaInfo {
  column_name: string;
  ordinal_position: number;
  data_type: string;
  native_type: string;
  character_max_length: number | null;
  numeric_precision: number | null;
  numeric_scale: number | null;
  is_nullable: boolean;
  column_default: string | null;
  column_comment: string;
  is_primary_key: boolean;
  column_key_type: string | null;
}

export interface ForeignKeyInfo {
  constraint_name: string;
  source_column_name: string;
  target_table_name: string;
  target_column_name: string;
}

export interface TableDetailResult {
  _id: string;
  table_name: string;
  table_comment: string;
  row_count_estimate: number;
  catalog_version: number;
  columns: ColumnMetaInfo[];
  foreignKeys: ForeignKeyInfo[];
}

export function getTableDetail(tableCatalogId: string) {
  return post<TableDetailResult>('/getTableDetail', { tableCatalogId });
}

// ─── ER Diagram API ──────────────────────────────────────────────

export interface ErNode {
  id: string;
  table_name: string;
  table_comment: string;
  key_columns: Array<{ name: string; type: 'PK' | 'FK' }>;
}

export interface ErEdge {
  id: string;
  source_node_id: string;
  source_column: string;
  target_node_id: string;
  target_column: string;
}

export function getErDiagramData(connectionId: string) {
  return post<{ nodes: ErNode[]; edges: ErEdge[]; total_nodes: number; total_edges: number }>('/getErDiagramData', { connectionId });
}

// ─── Mapping API ─────────────────────────────────────────────────

export interface FkFieldMapping {
  suffix: string;
  label: string;
  ref_column: string;
  enabled: boolean;
  data_type: string;
  data_length: number;
}

export interface ColumnSuggestion {
  source_column_name: string;
  source_data_type: string;
  source_column_comment?: string;
  target_column_name: string;
  target_data_type: string;
  target_column_length: number | null;
  target_precision?: number | null;
  target_scale?: number | null;
  target_is_nullable: boolean;
  target_default_value: string | null;
  target_is_index: boolean;
  target_column_comment?: string;
  transform_rule: 'direct' | 'type_cast' | 'custom';
  mapping_status: 'auto' | 'manual' | 'unmapped';
  suggestion_reason?: string;
  is_dict_field?: boolean;
  dict_table_name?: string;
  dict_type_column?: string;
  dict_type_value?: string;
  dict_code_column?: string;
  dict_value_column?: string;
  dict_companion_column_name?: string;
  // FK companion column fields
  is_fk_field?: boolean;
  fk_type?: 'person' | 'department';
  fk_ref_table?: string;
  fk_ref_pk_column?: string;
  fk_field_mappings?: FkFieldMapping[];
  fk_companion_of?: string;
  fk_ref_value_column?: string;
  fk_companion_suffix?: string;
}

export interface IndexSuggestion {
  columns: string[];
  is_unique: boolean;
  reason?: string;
  source_index_type?: 'primary_key' | 'unique' | 'foreign_key' | 'system' | null;
}

export function generateNormalizedTable(params: { tableCatalogId: string; targetConnectionId: string }) {
  return post<{
    source_table: { table_name: string; table_comment: string; columns: Array<ColumnMetaInfo & { column_key_type?: string | null }> };
    suggested_table: {
      normalized_table_name: string;
      columns: ColumnSuggestion[];
      suggested_indexes: IndexSuggestion[];
    };
    sync_identity_column: string;
    source_connection_id?: string;
    existing_normalized_table_id?: string;
    existing_creation_status?: string;
    existing_tenant_code?: string;
  }>('/generateNormalizedTable', params);
}

export interface SaveColumnMappingParams {
  normalizedTableId?: string | null;
  tableCatalogId: string;
  sourceConnectionId: string;
  targetConnectionId: string;
  normalizedTableName: string;
  syncIdentityColumn: string;
  syncIdentitySource: 'primary_key' | 'manual';
  tenantCode?: string;
  mappings: ColumnSuggestion[];
}

export function saveColumnMapping(params: SaveColumnMappingParams) {
  return post<{ normalizedTableId: string; mapping_count: number; creation_status: string }>('/saveColumnMapping', params);
}

export function createNormalizedTable(normalizedTableId: string) {
  return post<{
    normalizedTableId: string;
    normalized_table_name: string;
    creation_status: string;
    schema_version: number;
    column_count: number;
    index_count: number;
    executed_ddl: string;
  }>('/createNormalizedTable', { normalizedTableId });
}

export function resetNormalizedTable(normalizedTableId: string) {
  return post<{
    normalizedTableId: string;
    dropped_table: boolean;
    deleted_sync_tasks: number;
    deleted_failure_logs: number;
    creation_status: string;
  }>('/resetNormalizedTable', { normalizedTableId });
}

// ─── Normalized Table API ────────────────────────────────────────

export interface NormalizedTableListItem {
  _id: string;
  normalized_table_name: string;
  source_table_catalog_id: string;
  source_table_name: string;
  source_connection_id: string;
  source_connection_name: string;
  target_connection_id: string;
  target_connection_name: string;
  creation_status: string;
  schema_version: number;
  // 型录元数据字段（015-norm-table-catalog）
  display_name: string;
  description: string;
  group_id: string | null;
  sort_order: number | null;
  has_prod_mapping: boolean;
  create_time: number;
  last_sync_status: string | null;
  last_sync_time: number | null;
  last_sync_success_count: number;
}

export interface NormalizedGroup {
  _id: string;
  group_name: string;
  sort_order: number;
  table_count: number;
}

export function getNormalizedTableList(params: {
  sourceConnectionId?: string;
  creationStatus?: string;
  groupId?: string | null;
  page?: number;
  pageSize?: number;
} = {}) {
  return post<{
    items: NormalizedTableListItem[];
    total: number;
    page: number;
    pageSize: number;
  }>('/getNormalizedTableList', params);
}

// ─── Normalized Catalog Group API ────────────────────────────────

export function saveNormalizedGroup(params: {
  groupId?: string;
  groupName: string;
  sortOrder?: number;
}) {
  return post<{ _id: string; group_name: string; sort_order: number }>('/saveNormalizedGroup', params);
}

export function deleteNormalizedGroup(params: { groupId: string }) {
  return post<{ deleted_group_id: string; tables_ungrouped: number }>('/deleteNormalizedGroup', params);
}

export function getNormalizedGroups() {
  return post<{ groups: NormalizedGroup[]; unclassified_count: number }>('/getNormalizedGroups', {});
}

export function updateNormalizedTableMeta(params: {
  updates: Array<{
    normalizedTableId: string;
    display_name?: string;
    description?: string;
    group_id?: string | null;
    sort_order?: number | null;
  }>;
}) {
  return post<{
    updated_count: number;
    results: Array<{ normalizedTableId: string; status: string }>;
  }>('/updateNormalizedTableMeta', params);
}

export function extendNormalizedTableSchema(params: {
  normalizedTableId: string;
  newColumns: ColumnSuggestion[];
}) {
  return post<{
    normalizedTableId: string;
    new_columns_count: number;
    schema_version: number;
  }>('/extendNormalizedTableSchema', params);
}

export function updateColumnMapping(params: {
  normalizedTableId: string;
  mappings: Array<{
    target_column_name: string;
    source_column_name: string;
    source_data_type?: string;
    transform_rule?: string;
    mapping_status?: string;
    dict_config?: any;
    fk_config?: any;
  }>;
}) {
  return post<{
    normalizedTableId: string;
    results: Array<{ target: string; status: string }>;
  }>('/updateColumnMapping', params);
}

// ─── Sync API ────────────────────────────────────────────────────

export function startSyncTask(params: { normalizedTableId: string; syncType?: 'full' | 'incremental' }) {
  return post<{
    syncTaskId: string;
    normalized_table_name: string;
    sync_type: string;
    status: string;
    start_time: number;
    dependency_chain: string[];
  }>('/startSyncTask', params);
}

export interface SyncTaskStatus {
  _id: string;
  sync_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'partial';
  start_time: number;
  end_time?: number;
  duration_ms?: number;
  total_count: number;
  success_count: number;
  failure_count: number;
  skip_count?: number;
  progress_percent: number;
  failures?: Array<{ source_record_id: string; failure_reason: string }>;
}

export function getSyncTaskStatus(syncTaskId: string) {
  return post<SyncTaskStatus>('/getSyncTaskStatus', { syncTaskId });
}

export interface SyncTaskListItem {
  _id: string;
  sync_type: string;
  status: string;
  start_time: number;
  end_time: number;
  total_count: number;
  success_count: number;
  failure_count: number;
  trigger_user: string;
  /** Present only when normalizedTableId is omitted (global mode) */
  normalized_table_id?: string;
  normalized_table_name?: string;
}

export function getSyncTaskList(params: { normalizedTableId?: string; page?: number; pageSize?: number }) {
  return post<{
    items: SyncTaskListItem[];
    total: number;
    page: number;
    pageSize: number;
  }>('/getSyncTaskList', params);
}

// ─── Audit API ───────────────────────────────────────────────────

export function getSyncedData(params: {
  normalizedTableId: string;
  viewMode?: 'raw' | 'readable';
  page?: number;
  pageSize?: number;
  keyword?: string;
}) {
  return post<{
    columns: Array<{ name: string; type: string; label: string; resolved_label?: string }>;
    rows: Record<string, any>[];
    unresolved_fields?: string[];
    total: number;
    page: number;
    pageSize: number;
  }>('/getSyncedData', params);
}

export interface DictMappingEntry {
  source_dict_value: string;
  source_dict_label?: string;
  local_dict_key: string;
  local_dict_value: string;
  local_dict_label?: string;
}

export function saveDictMapping(params: {
  connectionId: string;
  source_dict_key: string;
  mappings: DictMappingEntry[];
}) {
  return post<{ saved_count: number; updated_count: number; source_dict_key: string }>('/saveDictMapping', params);
}

export interface DictMappingItem {
  _id: string;
  source_dict_key: string;
  source_dict_value: string;
  source_dict_label: string;
  local_dict_key: string;
  local_dict_value: string;
  local_dict_label: string;
}

export function getDictMappingList(params: { connectionId: string; source_dict_key?: string }) {
  return post<{ items: DictMappingItem[]; total: number }>('/getDictMappingList', params);
}

// ─── Production Write API ───────────────────────────────────────

export interface ProdTableInfo {
  table_name: string;
  table_comment: string;
  row_count_estimate: number;
  column_count: number;
}

export function getProdTableList(params: { prodConnectionId: string; keyword?: string }) {
  return post<{ tables: ProdTableInfo[]; total: number }>('/getProdTableList', params);
}

export interface ProdColumnInfo {
  column_name: string;
  data_type: string;
  native_type: string;
  is_nullable: boolean;
  is_primary_key: boolean;
  column_comment: string;
  column_default: string | null;
}

export function getProdTableSchema(params: { prodConnectionId: string; tableName: string }) {
  return post<{
    table_name: string;
    table_comment: string;
    columns: ProdColumnInfo[];
    primary_key_column: string | null;
  }>('/getProdTableSchema', params);
}

export interface ProdColumnBinding {
  source_column_name: string;
  source_data_type: string;
  prod_column_name: string;
  prod_data_type: string;
  type_mismatch: boolean;
  transform_rule: 'direct' | 'type_cast' | 'custom';
  transform_expression?: string | null;
  is_dict_field: boolean;
  dict_companion_column_name?: string;
  is_fk_field: boolean;
}

export interface SaveProdWriteMappingParams {
  writeMappingId?: string | null;
  normalizedTableId: string;
  prodConnectionId: string;
  prodTableName: string;
  bindings: ProdColumnBinding[];
}

export function saveProdWriteMapping(params: SaveProdWriteMappingParams) {
  return post<{
    writeMappingId: string;
    binding_count: number;
    mapping_status: string;
    has_dict_fields: boolean;
    dict_field_count: number;
    has_fk_fields: boolean;
    fk_field_count: number;
  }>('/saveProdWriteMapping', params);
}

export interface ProdWriteMappingInfo {
  _id: string;
  normalized_table_id: string;
  normalized_table_name: string;
  prod_connection_id: string;
  prod_table_name: string;
  mapping_status: string;
  has_dict_fields: boolean;
  dict_config_status: string;
  has_fk_fields: boolean;
  fk_config_status: string;
  last_write_time: number | null;
}

export function getProdWriteMapping(params: { normalizedTableId: string }) {
  return post<{ mapping: ProdWriteMappingInfo | null; bindings: ProdColumnBinding[]; fk_configs?: ProdFkConfigItem[] }>('/getProdWriteMapping', params);
}

export function getSourceDictValues(params: { normalizedTableId: string; columnName: string }) {
  return post<{ values: Array<{ value: string; count: number }>; total_distinct: number }>('/getSourceDictValues', params);
}

export interface ProdDictMappingEntry {
  source_dict_value: string;
  source_dict_label?: string;
  prod_dict_value: string;
  prod_dict_label?: string;
  mapping_status: 'mapped' | 'unmapped' | 'skipped';
}

export function saveProdDictMapping(params: {
  writeMappingId: string;
  sourceColumnName: string;
  prodColumnName: string;
  dictMappings: ProdDictMappingEntry[];
}) {
  return post<{ saved_count: number; mapping_status: string; dict_config_status: string }>('/saveProdDictMapping', params);
}

export interface ProdDictMappingItem {
  source_column_name: string;
  prod_column_name: string;
  source_dict_value: string;
  source_dict_label: string;
  prod_dict_value: string;
  prod_dict_label: string;
  mapping_status: string;
}

export function getProdDictMapping(params: { writeMappingId: string; sourceColumnName?: string }) {
  return post<{ items: ProdDictMappingItem[]; total: number }>('/getProdDictMapping', params);
}

// ─── Write Execution API ────────────────────────────────────────

export interface DryRunPreviewRow {
  source_row: Record<string, any>;
  transformed_row: Record<string, any>;
  errors: string[];
}

export interface DryRunResult {
  preview_rows: DryRunPreviewRow[];
  total_source_rows: number;
  preview_count: number;
  has_errors: boolean;
  error_count: number;
}

export function dryRunProdWrite(params: { writeMappingId: string; previewRows?: number }) {
  return post<DryRunResult>('/dryRunProdWrite', params);
}

export interface StartProdWriteResult {
  writeTaskId: string;
  write_type: string;
  status: string;
  start_time: number;
  total_count: number;
}

export function startProdWrite(params: { writeMappingId: string; writeType?: 'full' | 'incremental' }) {
  return post<StartProdWriteResult>('/startProdWrite', params);
}

export interface WriteTaskStatus {
  _id: string;
  write_type: string;
  status: 'running' | 'completed' | 'failed' | 'partial' | 'rolling_back' | 'rolled_back';
  start_time: number;
  end_time: number | null;
  total_count: number;
  success_count: number;
  failure_count: number;
  skip_count: number;
  progress_percent: number;
  error_message: string | null;
  failures: Array<{ source_record_id: string; failure_reason: string }>;
}

export function getWriteTaskStatus(writeTaskId: string) {
  return post<WriteTaskStatus>('/getWriteTaskStatus', { writeTaskId });
}

export interface WriteTaskListItem {
  _id: string;
  write_mapping_id: string;
  normalized_table_id: string;
  prod_table_name: string;
  write_type: string;
  status: string;
  start_time: number;
  end_time: number;
  total_count: number;
  success_count: number;
  failure_count: number;
  trigger_user: string;
}

export function getWriteTaskList(params: { writeMappingId?: string; normalizedTableId?: string; page?: number; pageSize?: number }) {
  return post<{
    items: WriteTaskListItem[];
    total: number;
    page: number;
    pageSize: number;
  }>('/getWriteTaskList', params);
}

// ─── Rollback & Validation API ──────────────────────────────────

export interface RollbackResult {
  writeTaskId: string;
  status: string;
  rollback_count: number;
  rollback_fail_count: number;
}

export function rollbackProdWrite(writeTaskId: string) {
  return post<RollbackResult>('/rollbackProdWrite', { writeTaskId });
}

export interface ValidationIssue {
  type: string;
  source_column?: string;
  prod_column?: string;
  expected_type?: string;
  actual_type?: string;
  message: string;
}

export interface ValidateResult {
  valid: boolean;
  issues: ValidationIssue[];
  binding_count: number;
  prod_column_count: number;
}

export function validateProdMapping(writeMappingId: string) {
  return post<ValidateResult>('/validateProdMapping', { writeMappingId });
}

// ─── FK Mapping API ────────────────────────────────────────────

export interface DetectedFkField {
  source_column_name: string;
  ref_table_name: string;
  ref_column_name: string;
  ref_table_display: string;
  prod_ref_table_exists: boolean;
  prod_ref_table_name: string | null;
  suggested_match_columns: string[];
}

export function detectFkFields(params: {
  normalized_table_id: string;
  prod_connection_id: string;
  prod_table_name: string;
}) {
  return post<{ fk_fields: DetectedFkField[] }>('/detectFkFields', params);
}

export interface FkManualMappingEntry {
  source_value: string;
  prod_value: string;
  source_display?: string;
  prod_display?: string;
}

export interface ProdFkConfigItem {
  _id: string;
  source_column_name: string;
  resolve_mode: 'auto_lookup' | 'manual';
  source_ref_table: string;
  source_ref_pk_column: string;
  source_match_column: string;
  prod_ref_table: string;
  prod_ref_pk_column: string;
  prod_match_column: string;
  manual_mappings: FkManualMappingEntry[];
}

export interface SaveFkConfigEntry {
  fk_config_id?: string;
  source_column_name: string;
  resolve_mode: 'auto_lookup' | 'manual' | 'person_match' | 'dept_match';
  source_ref_table?: string;
  source_ref_pk_column?: string;
  source_match_column?: string;
  prod_ref_table?: string;
  prod_ref_pk_column?: string;
  prod_match_column?: string;
  manual_mappings?: FkManualMappingEntry[];
}

export function saveProdFkConfig(params: {
  write_mapping_id: string;
  fk_configs: SaveFkConfigEntry[];
}) {
  return post<{
    saved_configs: Array<{ fk_config_id: string; source_column_name: string; resolve_mode: string }>;
    fk_config_status: string;
    mapping_status: string;
  }>('/saveProdFkConfig', params);
}

export function getProdFkConfig(params: { write_mapping_id: string }) {
  return post<{ fk_configs: ProdFkConfigItem[] }>('/getProdFkConfig', params);
}

// ─── Dict Table Meta API ──────────────────────────────────────

export interface DictTableMetaColumn {
  column_name: string;
  data_type: string;
}

export interface DictTableMeta {
  columns: DictTableMetaColumn[];
  distinct_types: string[];
}

export function getDictTableMeta(params: { connectionId: string; tableName: string; typeColumn?: string }) {
  return post<DictTableMeta>('/getDictTableMeta', params);
}

// ─── FK Companion Template API ──────────────────────────────────

export interface FkCompanionTemplate {
  _id: string;
  tenant_id: string;
  connection_id: string;
  fk_type: 'person' | 'department';
  ref_table_name: string;
  ref_pk_column: string;
  field_mappings: FkFieldMapping[];
}

export function getFkCompanionTemplate(params: { connectionId: string; fkType: 'person' | 'department' }) {
  return post<{ template: FkCompanionTemplate | null }>('/getFkCompanionTemplate', params, { silent: true });
}

export function saveFkCompanionTemplate(params: {
  connectionId: string;
  fkType: 'person' | 'department';
  refTableName: string;
  refPkColumn: string;
  fieldMappings: FkFieldMapping[];
}) {
  return post<{ templateId: string }>('/saveFkCompanionTemplate', params, { silent: true });
}

// ─── FK Auto-Match & Manual Designation API ────────────────────

export interface MatchResult {
  total: number;
  auto_matched: number;
  unmatched: number;
  unmatched_list: Array<{
    source_person_name?: string;
    source_person_phone?: string;
    source_dept_name?: string;
    match_status: string;
  }>;
}

export function matchPersonRecords(params: { writeMappingId: string; sourceColumnName: string }) {
  return post<MatchResult>('/matchPersonRecords', params);
}

export function matchDeptRecords(params: { writeMappingId: string; sourceColumnName: string }) {
  return post<MatchResult>('/matchDeptRecords', params);
}

export interface ManualFkMatchEntry {
  source_value_key: string;
  prod_id: string;
  prod_display_name: string;
}

export function saveManualFkMatch(params: {
  writeMappingId: string;
  sourceColumnName: string;
  fkType: 'person' | 'department';
  matches: ManualFkMatchEntry[];
}) {
  return post<{ updated: number; remaining_unmatched: number; fk_config_status: string }>('/saveManualFkMatch', params);
}

export interface ProdPersonItem {
  id: string;
  name: string;
  phone: string;
  nickName: string;
  uuid: string;
  departmentId: string;
  departmentName: string;
}

export function searchProdPersons(params: { keyword: string; page?: number; pageSize?: number }) {
  return post<{ items: ProdPersonItem[]; total: number; page: number; pageSize: number }>('/searchProdPersons', params);
}

export interface ProdDeptItem {
  id: string;
  name: string;
  departmentCode: string;
  departmentParentCode: string;
  depth: number;
}

export function searchProdDepts(params: { keyword: string; page?: number; pageSize?: number }) {
  return post<{ items: ProdDeptItem[]; total: number; page: number; pageSize: number }>('/searchProdDepts', params);
}

export interface ProdDictItem {
  dict_code: string;
  dict_label: string;
}

export function getProdDictItems(params: { prodConnectionId: string; dictType: string; dictTableName?: string }) {
  return post<{ items: ProdDictItem[] }>('/getProdDictItems', params);
}

// ─── Catalog Group API ──────────────────────────────────────────

export interface CatalogGroupItem {
  _id: string;
  group_name: string;
  sort_order: number;
  table_count: number;
}

export function getCatalogGroups(params: { connectionId: string }) {
  return post<{ items: CatalogGroupItem[] }>('/getCatalogGroups', params);
}

export function saveCatalogGroup(params: { groupId?: string; connectionId: string; groupName: string; sortOrder?: number }) {
  return post<{ _id: string; group_name: string; sort_order: number }>('/saveCatalogGroup', params);
}

export function deleteCatalogGroup(params: { groupId: string }) {
  return post<{ deleted_group_id: string; tables_ungrouped: number }>('/deleteCatalogGroup', params);
}

export interface UpdateTableCatalogMetaItem {
  tableCatalogId: string;
  displayName?: string;
  groupId?: string | null;
  syncOrder?: number | null;
}

export function updateTableCatalogMeta(params: { updates: UpdateTableCatalogMetaItem[] }) {
  return post<{ updated: number }>('/updateTableCatalogMeta', params);
}
